import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Star, ChevronLeft, ChevronRight,
  Volume2, VolumeX, Home, RotateCcw, Award, CheckCircle, X,
} from "lucide-react";

const B = {
  border: "4px solid #29294d",
  shadow: "8px 8px 0px #29294d",
  shadowSm: "5px 5px 0px #29294d",
  font: "'Space Grotesk', sans-serif",
};

const PINK = "#ff6b9d";
const YELLOW = "#ffd93d";
const BLUE = "#4d96ff";
const PURPLE = "#9b5de5";
const GREEN = "#6bcb77";
const NAVY = "#29294d";
const CREAM = "#fff9e6";
const ORANGE = "#ff9f43";

// Story pages with illustrations (using emojis and styled divs)
const STORY_PAGES = [
  {
    id: 1,
    bg: "linear-gradient(180deg, #87CEEB 0%, #E0F4FF 100%)",
    title: null,
    text: "Lucía era una niña de 5 años que vivía con su familia y su perrita **Canela**.",
    highlight: "Canela",
  },
  {
    id: 2,
    bg: "linear-gradient(180deg, #FFE4B5 0%, #FFF8DC 100%)",
    title: null,
    text: "Canela era pequeña, de color marrón clarito, y le encantaba jugar con Lucía en el jardín.",
    highlight: "jugar",
  },
  {
    id: 3,
    bg: "linear-gradient(180deg, #B0C4DE 0%, #D3D3D3 100%)",
    title: null,
    text: "Un día, el cielo se puso muy **oscuro**. Las nubes eran grandes y grises.",
    highlight: "oscuro",
  },
  {
    id: 4,
    bg: "linear-gradient(180deg, #778899 0%, #A9A9A9 100%)",
    title: null,
    text: "De repente... ¡¡¡BOOM!!! Un trueno muy fuerte sonó en el cielo.",
    highlight: "BOOM",
    shake: true,
  },
  {
    id: 5,
    bg: "linear-gradient(180deg, #DDA0DD 0%, #E6E6FA 100%)",
    title: null,
    text: "Canela se asustó mucho. Se escondió debajo de la cama temblando.",
    highlight: "asustó",
  },
  {
    id: 6,
    bg: "linear-gradient(180deg, #FFB6C1 0%, #FFF0F5 100%)",
    title: null,
    text: "Lucía también tenía un poquito de miedo, pero recordó lo que mamá le había enseñado.",
    highlight: "recordó",
  },
  {
    id: 7,
    bg: "linear-gradient(180deg, #98FB98 0%, #F0FFF0 100%)",
    title: "¡Idea!",
    text: '"¡Ya sé! Tengo que cuidar de Canela porque ella también tiene miedo", pensó Lucía.',
    highlight: "cuidar",
  },
  {
    id: 8,
    bg: "linear-gradient(180deg, #FAFAD2 0%, #FFFACD 100%)",
    title: null,
    text: "Lucía buscó la mantita favorita de Canela y su juguete preferido.",
    highlight: "mantita",
  },
  {
    id: 9,
    bg: "linear-gradient(180deg, #E0FFFF 0%, #F0FFFF 100%)",
    title: null,
    text: "Se sentó junto a Canela y la abrazó con mucho cariño. Le habló bajito:",
    highlight: "abrazó",
  },
  {
    id: 10,
    bg: "linear-gradient(180deg, #FFE4E1 0%, #FFF5EE 100%)",
    title: null,
    text: '"No tengas miedo, Canela. Yo estoy aquí contigo. Estamos a salvo en casa."',
    highlight: "a salvo",
    speech: true,
  },
  {
    id: 11,
    bg: "linear-gradient(180deg, #F0E68C 0%, #FFFACD 100%)",
    title: null,
    text: "Poco a poco, Canela dejó de temblar. El calor de Lucía la hacía sentir segura.",
    highlight: "segura",
  },
  {
    id: 12,
    bg: "linear-gradient(180deg, #87CEEB 0%, #E0F4FF 100%)",
    title: null,
    text: "Cuando la tormenta terminó, salió un arcoíris precioso en el cielo.",
    highlight: "arcoíris",
  },
  {
    id: 13,
    bg: "linear-gradient(180deg, #98FB98 0%, #F5FFFA 100%)",
    title: "¡Bien hecho!",
    text: "Mamá le dijo: \"¡Muy bien, Lucía! Has cuidado de Canela como una verdadera heroína.\"",
    highlight: "heroína",
  },
  {
    id: 14,
    bg: "linear-gradient(180deg, #FFD700 0%, #FFFACD 100%)",
    title: "FIN",
    text: "Y Lucía aprendió que cuando hay una tormenta, los animales también necesitan amor y protección.",
    highlight: "amor y protección",
    isEnd: true,
  },
];

const QUIZ_QUESTIONS = [
  {
    question: "¿Cómo se llama la perrita de Lucía?",
    options: ["Canela", "Luna", "Coco"],
    correct: 0,
  },
  {
    question: "¿Qué hizo Canela cuando escuchó el trueno?",
    options: ["Ladró muy fuerte", "Se escondió asustada", "Salió a jugar"],
    correct: 1,
  },
  {
    question: "¿Qué hizo Lucía para ayudar a Canela?",
    options: ["La dejó sola", "La abrazó y le habló bajito", "Se fue a dormir"],
    correct: 1,
  },
  {
    question: "¿Qué salió en el cielo después de la tormenta?",
    options: ["Más nubes", "Un arcoíris", "La luna"],
    correct: 1,
  },
];

export default function CuentoLluviaPage() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizStep, setQuizStep] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [earnedStars, setEarnedStars] = useState(0);

  const page = STORY_PAGES[currentPage];
  const totalPages = STORY_PAGES.length;

  const nextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    } else {
      setShowQuiz(true);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleAnswer = (answerIndex) => {
    setSelectedAnswer(answerIndex);
    setShowResult(true);
    
    if (answerIndex === QUIZ_QUESTIONS[quizStep].correct) {
      setScore(score + 1);
    }

    setTimeout(() => {
      if (quizStep < QUIZ_QUESTIONS.length - 1) {
        setQuizStep(quizStep + 1);
        setSelectedAnswer(null);
        setShowResult(false);
      } else {
        const finalScore = answerIndex === QUIZ_QUESTIONS[quizStep].correct ? score + 1 : score;
        setEarnedStars(finalScore);
        setQuizComplete(true);
      }
    }, 1500);
  };

  const restartStory = () => {
    setCurrentPage(0);
    setShowQuiz(false);
    setQuizStep(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setQuizComplete(false);
    setEarnedStars(0);
  };

  // Render Quiz Complete Screen
  if (quizComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: `linear-gradient(180deg, ${PURPLE} 0%, ${PINK} 100%)` }}>
        <div className="max-w-md w-full text-center p-8" style={{ background: "white", border: B.border, boxShadow: B.shadow, borderRadius: "24px" }}>
          <Star className="w-16 h-16 mx-auto mb-4" style={{ color: "#FFD93D", fill: "#FFD93D" }} />
          <h2 className="text-3xl font-bold mb-4" style={{ color: NAVY, fontFamily: B.font }}>
            ¡FELICIDADES!
          </h2>
          <p className="text-lg mb-6" style={{ color: NAVY }}>
            Has completado el cuento y el quiz
          </p>
          
          <div className="flex justify-center gap-2 mb-6">
            {[1, 2, 3, 4].map((star) => (
              <Star
                key={star}
                className="w-12 h-12 transition-all duration-300"
                style={{ 
                  color: star <= earnedStars ? YELLOW : "#ddd",
                  fill: star <= earnedStars ? YELLOW : "none",
                  transform: star <= earnedStars ? "scale(1.1)" : "scale(1)",
                }}
              />
            ))}
          </div>
          
          <p className="text-lg font-bold mb-8" style={{ color: PURPLE }}>
            ¡Has ganado {earnedStars} {earnedStars === 1 ? "estrella" : "estrellas"}!
          </p>

          {earnedStars === 4 && (
            <div className="mb-6 p-4" style={{ background: YELLOW + "30", border: `3px solid ${YELLOW}`, borderRadius: "16px" }}>
              <Award className="w-10 h-10 mx-auto mb-2" style={{ color: ORANGE }} />
              <p className="font-bold" style={{ color: NAVY }}>¡INSIGNIA DESBLOQUEADA!</p>
              <p className="text-sm" style={{ color: NAVY }}>Protector/a de Tormentas</p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <button
              onClick={restartStory}
              className="w-full py-4 font-bold uppercase tracking-wider text-lg transition-all hover:-translate-y-1"
              style={{ background: BLUE, color: "white", border: B.border, boxShadow: B.shadowSm, borderRadius: "12px", fontFamily: B.font }}
            >
              <RotateCcw className="inline w-5 h-5 mr-2" /> LEER OTRA VEZ
            </button>
            <button
              onClick={() => navigate("/aprende/kids")}
              className="w-full py-4 font-bold uppercase tracking-wider text-lg transition-all hover:-translate-y-1"
              style={{ background: PINK, color: "white", border: B.border, boxShadow: B.shadowSm, borderRadius: "12px", fontFamily: B.font }}
            >
              <Home className="inline w-5 h-5 mr-2" /> MÁS CUENTOS
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render Quiz
  if (showQuiz) {
    const question = QUIZ_QUESTIONS[quizStep];
    return (
      <div className="min-h-screen p-6" style={{ background: `linear-gradient(180deg, ${BLUE} 0%, ${PURPLE} 100%)` }}>
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => setShowQuiz(false)} className="flex items-center gap-2 text-white font-bold">
              <ArrowLeft className="w-5 h-5" /> Volver al cuento
            </button>
            <div className="flex items-center gap-2 px-4 py-2" style={{ background: "white", border: B.border, borderRadius: "12px" }}>
              <span className="font-bold" style={{ color: NAVY }}>Pregunta {quizStep + 1}/{QUIZ_QUESTIONS.length}</span>
            </div>
          </div>

          {/* Question Card */}
          <div className="p-8" style={{ background: "white", border: B.border, boxShadow: B.shadow, borderRadius: "24px" }}>
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8" style={{ color: NAVY, fontFamily: B.font }}>
              {question.question}
            </h2>

            <div className="space-y-4">
              {question.options.map((option, i) => {
                let bg = "white";
                let borderColor = NAVY;
                let textColor = NAVY;

                if (showResult) {
                  if (i === question.correct) {
                    bg = GREEN;
                    borderColor = GREEN;
                    textColor = "white";
                  } else if (i === selectedAnswer && i !== question.correct) {
                    bg = PINK;
                    borderColor = PINK;
                    textColor = "white";
                  }
                }

                return (
                  <button
                    key={i}
                    onClick={() => !showResult && handleAnswer(i)}
                    disabled={showResult}
                    className="w-full p-5 text-left text-xl font-bold transition-all duration-200 hover:-translate-y-1 disabled:hover:translate-y-0 flex items-center gap-4"
                    style={{ 
                      background: bg, 
                      border: `4px solid ${borderColor}`, 
                      borderRadius: "16px",
                      color: textColor,
                      fontFamily: B.font,
                      boxShadow: B.shadowSm,
                    }}
                  >
                    <span className="w-10 h-10 flex items-center justify-center text-lg" style={{ background: showResult && i === question.correct ? "white" : YELLOW, borderRadius: "50%", color: NAVY }}>
                      {showResult ? (i === question.correct ? <CheckCircle className="w-6 h-6" style={{ color: GREEN }} /> : i === selectedAnswer ? <X className="w-6 h-6" style={{ color: PINK }} /> : String.fromCharCode(65 + i)) : String.fromCharCode(65 + i)}
                    </span>
                    {option}
                  </button>
                );
              })}
            </div>

            {showResult && (
              <div className="mt-6 text-center">
                <p className="text-xl font-bold" style={{ color: selectedAnswer === question.correct ? GREEN : PINK }}>
                  {selectedAnswer === question.correct ? "¡Muy bien!" : "¡Casi! Sigue intentando"}
                </p>
              </div>
            )}
          </div>

          {/* Progress */}
          <div className="mt-8 flex justify-center gap-2">
            {QUIZ_QUESTIONS.map((_, i) => (
              <div
                key={i}
                className="w-4 h-4 rounded-full"
                style={{ 
                  background: i < quizStep ? GREEN : i === quizStep ? YELLOW : "rgba(255,255,255,0.3)",
                  border: "2px solid white",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render Story Page
  return (
    <div className="min-h-screen flex flex-col" style={{ background: page.bg, fontFamily: B.font }}>
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between" style={{ background: "rgba(255,255,255,0.9)", borderBottom: B.border }}>
        <button onClick={() => navigate("/aprende/kids")} className="flex items-center gap-2 font-bold" style={{ color: NAVY }}>
          <ArrowLeft className="w-5 h-5" /> VOLVER
        </button>
        <div className="text-center">
          <h1 className="text-lg font-bold" style={{ color: NAVY }}>El día que llovió mucho</h1>
          <p className="text-xs" style={{ color: PINK }}>Lucía y Canela</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1" style={{ background: YELLOW, border: "3px solid " + NAVY, borderRadius: "8px" }}>
          <span className="font-bold text-sm" style={{ color: NAVY }}>{currentPage + 1}/{totalPages}</span>
        </div>
      </header>

      {/* Story Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          {/* Illustration */}
          <div className={`flex justify-center mb-8 ${page.shake ? 'animate-bounce' : ''}`}>
            <div 
              className="w-40 h-40 md:w-52 md:h-52 flex items-center justify-center text-7xl md:text-8xl"
              style={{ 
                background: page.illustrationBg, 
                border: B.border, 
                boxShadow: B.shadow, 
                borderRadius: "50%",
              }}
            >
              {page.illustration}
            </div>
          </div>

          {/* Title if exists */}
          {page.title && (
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" style={{ color: NAVY }}>
              {page.title}
            </h2>
          )}

          {/* Text */}
          <div 
            className={`p-6 md:p-8 text-center ${page.speech ? 'italic' : ''}`}
            style={{ 
              background: "white", 
              border: B.border, 
              boxShadow: B.shadowSm, 
              borderRadius: "20px",
            }}
          >
            <p className="text-xl md:text-2xl leading-relaxed" style={{ color: NAVY }}>
              {page.text.split('**').map((part, i) => 
                i % 2 === 1 ? (
                  <strong key={i} style={{ color: PINK }}>{part}</strong>
                ) : (
                  <span key={i}>{part}</span>
                )
              )}
            </p>
          </div>
        </div>
      </main>

      {/* Navigation */}
      <footer className="px-6 py-6" style={{ background: "rgba(255,255,255,0.9)", borderTop: B.border }}>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button
            onClick={prevPage}
            disabled={currentPage === 0}
            className="flex items-center gap-2 px-6 py-4 font-bold uppercase text-lg transition-all hover:-translate-y-1 disabled:opacity-30 disabled:hover:translate-y-0"
            style={{ 
              background: currentPage === 0 ? "#ddd" : BLUE, 
              color: "white", 
              border: B.border, 
              boxShadow: currentPage === 0 ? "none" : B.shadowSm, 
              borderRadius: "12px",
            }}
          >
            <ChevronLeft className="w-6 h-6" /> ATRÁS
          </button>

          {/* Page dots */}
          <div className="hidden md:flex gap-1">
            {STORY_PAGES.map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full cursor-pointer transition-all"
                onClick={() => setCurrentPage(i)}
                style={{ 
                  background: i === currentPage ? PINK : i < currentPage ? GREEN : "#ddd",
                  transform: i === currentPage ? "scale(1.5)" : "scale(1)",
                }}
              />
            ))}
          </div>

          <button
            onClick={nextPage}
            className="flex items-center gap-2 px-6 py-4 font-bold uppercase text-lg transition-all hover:-translate-y-1"
            style={{ 
              background: page.isEnd ? GREEN : PINK, 
              color: "white", 
              border: B.border, 
              boxShadow: B.shadowSm, 
              borderRadius: "12px",
            }}
          >
            {page.isEnd ? (
              <>¡QUIZ! <Star className="w-6 h-6" /></>
            ) : (
              <>SIGUIENTE <ChevronRight className="w-6 h-6" /></>
            )}
          </button>
        </div>
      </footer>
    </div>
  );
}
